<script setup lang="ts">
import type {
  ExternalCourse,
  SubjectRequest,
} from '@/models/current-student-advanced-standing-form/api'
import {
  externalCourseAllowsLtuSubjectRequests,
  externalCourseCreditMessage,
  studentRequestedCourseSubjectRequests,
} from '@/models/current-student-advanced-standing-form/previousLearning'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  courses: ExternalCourse[]
  rowClass: (course: ExternalCourse) => LegacyClassBinding
  rowspan: (course: ExternalCourse) => number
  isVerifiedStatus: (status: ExternalCourse['external_course_verified']) => boolean
  canEditExternalCourse: (course: ExternalCourse) => boolean
}>()

const emit = defineEmits<{
  editCourse: [course: ExternalCourse]
  addSubjectRequest: [course: ExternalCourse]
  deleteSubjectRequest: [course: ExternalCourse, request: SubjectRequest]
}>()
</script>

<template>
  <div class="table-responsive">
    <table class="table table-bordered">
      <thead>
        <tr>
          <th class="narrow"></th>
          <th>Institution Name</th>
          <th>Course Name</th>
          <th colspan="3">La Trobe subjects to request</th>
        </tr>
      </thead>
      <tbody id="ExternalCoursesTableBody">
        <template v-for="course in courses" :key="course.external_course_id">
          <tr :class="rowClass(course)">
            <td :rowspan="rowspan(course)" class="fit">
              <template v-if="isVerifiedStatus(course.external_course_verified)">
                Verified
              </template>
              <button v-else-if="canEditExternalCourse(course)" class="btn btn-primary" type="button"
                @click="emit('editCourse', course)">Edit</button>
            </td>
            <td :rowspan="rowspan(course)">{{ course.external_course_institution?.name ??
              course.external_course_institution_name }}
            </td>
            <td :rowspan="rowspan(course)">{{ course.external_course_name }}</td>
            <template v-if="externalCourseAllowsLtuSubjectRequests(course)">
              <td class="narrow" :rowspan="rowspan(course)">
                <button class="btn btn-primary btn-sm" title="Add new subject" type="button"
                  @click="emit('addSubjectRequest', course)">
                  &#x2b;
                </button>
              </td>
              <td v-if="studentRequestedCourseSubjectRequests(course).length === 0" colspan="2" class="text-danger">
                &#x2190; Please Add a Subject
              </td>
              <template v-else>
                <td>{{ studentRequestedCourseSubjectRequests(course)[0]?.subject_code }}</td>
                <td class="narrow">
                  <button v-if="studentRequestedCourseSubjectRequests(course)[0]" class="btn btn-danger btn-sm"
                    title="Delete Subject" type="button"
                    @click="emit('deleteSubjectRequest', course, studentRequestedCourseSubjectRequests(course)[0]!)">
                    &#x232b;
                  </button>
                </td>
              </template>
            </template>
            <td v-else colspan="3" :class="{
              'text-info': course.external_course_level_credit_requires_articulation === true
                && course.articulation_agreements.length > 0
            }">
              {{ externalCourseCreditMessage(course) }}
            </td>
          </tr>
          <tr v-for="subjectRequest in studentRequestedCourseSubjectRequests(course).slice(1)"
            :key="`${course.external_course_id}-${subjectRequest.subject_code}`">
            <td>{{ subjectRequest.subject_code }}</td>
            <td class="narrow">
              <button class="btn btn-danger btn-sm" title="Delete Subject" type="button"
                @click="emit('deleteSubjectRequest', course, subjectRequest)">
                &#x232b;
              </button>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
